/**
 * MiscellaneousPage
 *
 * Top-level "Miscellaneous" section that groups existing, otherwise standalone
 * tools under the standardized top sub-tab bar (SubPageShell):
 *   - Converter   (file/media conversion)
 *   - Translate   (subtitle/text translation)
 *   - Chat        (single-assistant chat)
 *
 * Each sub-tab renders its existing component unchanged. Only the active body
 * is mounted, wrapped in an <ErrorBoundary> so a crash in one tool never takes
 * down the whole section. Generation badges (Converter, Translate) come from
 * the shared GenerationContext; Chat has no async jobs so it carries no badge.
 *
 * NOTE: the "Group Chat" sub-tab is intentionally hidden (web AND desktop). Its
 * backend (/api/v1/chat/groups) is Cloud-Run-only, so the desktop client 404s
 * on every call and the feature is parked pending a rework. The <GroupChatTab>
 * component is kept for that future work — only its entry point here is
 * removed. It no longer has a real-time transport: the SSE route it used to
 * open was never implemented (and EventSource cannot forward the Firebase
 * token that /api/v1/chat/* requires), so it polls messages instead.
 */

import { useEffect, useState } from 'react';
import { RefreshCw, Languages, MessageSquare } from 'lucide-react';
import { SubPageShell, type SubTabItem } from '@/components/common';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useTranslation } from '@/hooks/useTranslation';
import { useGeneration } from '@/contexts/GenerationContext.context';
import type { GenerationTabMode } from '@/types/generationJob';
import { ConverterPage } from '@/pages/ConverterPage';
import { TranslateTab } from '@/components/translate';
import { ChatTab } from '@/components/chat';

interface MiscellaneousPageProps {
  /** Optional initial sub-tab (for future deep-links). Defaults to 'converter'. */
  initialSubTab?: string;
}

export function MiscellaneousPage({ initialSubTab }: MiscellaneousPageProps) {
  const { t } = useTranslation();
  const { activeCountsByTab } = useGeneration();
  const [active, setActive] = useState<string>(initialSubTab ?? 'converter');

  // React to deep-link changes after mount (e.g. navigating between sub-tabs
  // via an external link while the section is already open).
  useEffect(() => {
    if (initialSubTab) {
      setActive(initialSubTab);
    }
  }, [initialSubTab]);

  const converterBadge = activeCountsByTab.get('converter' as GenerationTabMode) ?? 0;
  const translateBadge = activeCountsByTab.get('translate' as GenerationTabMode) ?? 0;

  const tabs: SubTabItem[] = [
    {
      id: 'converter',
      label: t('app.header.converterTab', 'Converter'),
      icon: RefreshCw,
      badgeCount: converterBadge,
    },
    {
      id: 'translate',
      label: t('app.header.translateTab', 'Translate'),
      icon: Languages,
      badgeCount: translateBadge,
    },
    {
      id: 'chat',
      label: t('app.header.chatTab', 'Chat'),
      icon: MessageSquare,
    },
    // 'groupchat' sub-tab hidden pending rework (Cloud-only backend, see file header).
  ];

  const renderBody = () => {
    switch (active) {
      case 'converter':
        return (
          <ErrorBoundary section="converter" key="converter">
            <ConverterPage />
          </ErrorBoundary>
        );
      case 'translate':
        return (
          <ErrorBoundary section="translate" key="translate">
            <TranslateTab />
          </ErrorBoundary>
        );
      case 'chat':
        return (
          <ErrorBoundary section="chat" key="chat">
            <ChatTab />
          </ErrorBoundary>
        );
      // 'groupchat' case removed — sub-tab hidden pending rework (see file header).
      default:
        return null;
    }
  };

  return (
    <SubPageShell
      tabs={tabs}
      activeId={active}
      onChange={setActive}
      accent="purple"
      ariaLabel={t('app.header.miscellaneousTab', 'Miscellaneous')}
    >
      {renderBody()}
    </SubPageShell>
  );
}
